// components/FindProductRequestModal.jsx
import React, { useEffect, useState } from "react";
import ApiCustom from "../utils/ApiCustom";
import { toast } from "react-toastify";
import * as Yup from "yup";

// Yup schema
const schema = Yup.object({
  message: Yup.string()
    .trim()
    .required("Vui lòng nhập thông tin sản phẩm cần tìm."),
  fullName: Yup.string()
    .trim()
    .required("Vui lòng nhập họ và tên.")
    .max(255, "Họ và tên tối đa 255 ký tự."),
  phone: Yup.string()
    .trim()
    .required("Vui lòng nhập số điện thoại.")
    .matches(/^[0-9+().\-\s]{8,20}$/, "Số điện thoại không hợp lệ."),
});

export default function FindProductRequestModal({
  isOpen,
  onClose,
  prefillMessage = "",
}) {
  const [fullName, setFullName] = useState("");
  const [message, setMessage] = useState(prefillMessage || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFullName("");
      setMessage(prefillMessage || "");
      setPhone("");
      setErrors({});
    }
  }, [isOpen, prefillMessage]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const name = fullName.trim();
    const msg = message.trim();
    const ph = phone.trim();

    // Yup validate
    try {
      await schema.validate(
        { fullName: name, message: msg, phone: ph },
        { abortEarly: false },
      );
      setErrors({});
    } catch (validationErr) {
      const fieldErrors = {};
      if (validationErr?.inner?.length) {
        validationErr.inner.forEach((e) => {
          if (e.path && !fieldErrors[e.path]) fieldErrors[e.path] = e.message;
        });
      } else if (validationErr?.path) {
        fieldErrors[validationErr.path] = validationErr.message;
      }
      setErrors(fieldErrors);
      toast.error("Vui lòng kiểm tra các trường nhập.");
      return;
    }

    try {
      setLoading(true);
      await ApiCustom.post(
        "/product-request-create",
        {
          fullName: name,
          productInfo: msg,
          phone: ph,
          note: null,
        },
        { headers: { "Content-Type": "application/json" } },
      );

      toast.success("Đã ghi nhận yêu cầu. Cảm ơn bạn!");
      onClose?.();
    } catch (err) {
      const serverMsg =
        err?.response?.data?.messages ||
        err?.response?.data?.message ||
        "Gửi yêu cầu thất bại. Vui lòng thử lại!";
      toast.error(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  // helpers: clear field error on change
  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-3xl">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Yêu cầu tìm sản phẩm</h3>
          <button
            onClick={onClose}
            className="px-3 py-1 rounded hover:bg-base-200"
            aria-label="Đóng"
            title="Đóng"
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700">
            <em>
              Xin quý khách để lại thông tin sản phẩm cần tìm, họ tên và số điện
              thoại liên hệ để chúng tôi phản hồi. Xin cảm ơn.
            </em>
          </p>

          {/* 1) Thông tin sản phẩm cần tìm */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Thông tin sản phẩm cần tìm
            </label>
            <textarea
              className={`textarea textarea-bordered w-full ${
                errors.message ? "textarea-error" : ""
              }`}
              rows={4}
              placeholder="Ví dụ: Tên sản phẩm / mô tả sản phẩm…"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                clearError("message");
              }}
              disabled={loading}
            />
            {errors.message && (
              <p className="mt-1 text-sm text-red-600">{errors.message}</p>
            )}
          </div>

          {/* 2) Họ và tên (đã đưa xuống dưới thông tin cần tìm) */}
          <div>
            <label className="block text-sm font-medium mb-1">Họ và tên</label>
            <input
              type="text"
              className={`input input-bordered w-full ${
                errors.fullName ? "input-error" : ""
              }`}
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                clearError("fullName");
              }}
              disabled={loading}
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
            )}
          </div>

          {/* 3) Số điện thoại */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Số điện thoại liên hệ
            </label>
            <input
              type="tel"
              className={`input input-bordered w-full ${
                errors.phone ? "input-error" : ""
              }`}
              value={phone}
              onChange={(e) => {
                const val = e.target.value;
                setPhone(val);
                // ✅ Validate ngay khi onChange
                schema
                  .validateAt("phone", { phone: val })
                  .then(() =>
                    setErrors((prev) => ({ ...prev, phone: undefined })),
                  )
                  .catch((err) =>
                    setErrors((prev) => ({ ...prev, phone: err.message })),
                  );
              }}
              disabled={loading}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>
        </div>

        <div className="p-5 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            disabled={loading}
          >
            Đóng
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 rounded-md btn btn-outline btn-primary"
            disabled={loading}
          >
            {loading ? "Đang gửi..." : "Gửi yêu cầu"}
          </button>
        </div>
      </div>
    </div>
  );
}
